import React from 'react';
import { isEmpty } from 'lodash';
import Script from 'next/script';
import { Image, Spacer, Box } from '@zillow/constellation';
import {
    BlockList,
    PlacementProvider,
    initializeApollo,
    getPost,
    getBlocksData,
} from '@zillow/cms-blocks';

import { withPreload } from '@zillow/zrs-cms';

import ArticleAuthor from '../../components/ArticleAuthor';
import Hero from '../../components/Hero';
import Layout from '../../components/Layout';
import MasterLayout from '../../components/MasterLayout';
import Tags from '../../components/Tags';
import RelatedPosts from '../../components/RelatedPosts';
import LearningCenterMenu from '../../components/LearningCenterMenu';
import LearningCenterLayout from '../../components/LearningCenterLayout';

import {
    NEXT_PUBLIC_KNOTCH_LC_POST_ACCOUNT_ID,
    REVALIDATION_INTERVAL,
} from '../../../server/config';

const ImageWithPreload = withPreload(Image);

export type ContentProps = Awaited<
    ReturnType<typeof getStaticProps>
>['props'] & {
    preview?: boolean;
};

export const PostTemplate = ({ post }) => {
    const sidebar = (
        <React.Fragment>
            <ArticleAuthor
                avatar={
                    post?.author?.avatar ? (
                        <ImageWithPreload
                            src={post?.author?.avatar}
                            alt={post?.author?.name}
                        />
                    ) : undefined
                }
                name={post?.author?.name}
                jobTitle={post?.author?.jobTitle}
                publishDate={post.date}
            />
            <Spacer marginBottom="md" />
        </React.Fragment>
    );

    const postSidebar = post?.sidebar ? (
        <BlockList blocks={JSON.parse(post?.sidebar?.blocks || '[]')} />
    ) : null;

    const footer = post?.footer ? (
        <div>
            <BlockList blocks={JSON.parse(post?.footer?.blocks || '[]')} />
        </div>
    ) : null;

    return (
        <MasterLayout
            hasTags={!isEmpty(post?.postTags)}
            sidebar={sidebar}
            stickySidebar={postSidebar}
            footer={post?.footer ? footer : undefined}
            hero={<Hero post={post} />}
            relatedArticles={<RelatedPosts post={post} />}
        >
            <PlacementProvider placement="post">
                <BlockList blocks={post?.blocks || []} />
            </PlacementProvider>

            <div className="knotch_placeholder" />
            <Script
                src="https://www.knotch-cdn.com/unit/latest/knotch.min.js"
                data-account={NEXT_PUBLIC_KNOTCH_LC_POST_ACCOUNT_ID}
            />

            <Tags postTags={post?.postTags} />
        </MasterLayout>
    );
};

const PageTemplate = ({ post }) => (
    <React.Fragment>
        <PlacementProvider placement="page">
            <BlockList blocks={post?.blocks || []} />
        </PlacementProvider>
        {post?.footer && (
            <Box
                sx={{
                    margin: '0 auto',
                    padding: '0 $spacing.sm',
                    maxWidth: '$breakpoints.xxl',
                }}
            >
                <BlockList blocks={JSON.parse(post?.footer?.blocks || '[]')} />
            </Box>
        )}
    </React.Fragment>
);

function Post({ post, preview }) {
    return (
        <Layout
            head={post.head}
            NavComponent={LearningCenterMenu}
            menu={post?.menu}
            preview={preview}
        >
            <LearningCenterLayout>
                {post.type === 'post' ? (
                    <PostTemplate post={post} />
                ) : (
                    <PageTemplate post={post} />
                )}
            </LearningCenterLayout>
        </Layout>
    );
}

export async function getStaticProps({ params }) {
    const { slug = ['/'] } = params;

    initializeApollo();

    const { post, settings } = await getPost({
        variables: {
            site: 'LEARN',
            id: slug.join('/'),
            idType: 'URI',
            withSettings: true,
        },
        fetchPolicy: 'no-cache',
    });

    // Check for post status.
    if (post?.status !== 'publish') {
        return {
            notFound: true,
        };
    }

    const blocks = await getBlocksData(JSON.parse(post?.blocks || '[]'));
    const menu = post?.type === 'post' ? settings?.menu : post?.menu;

    return {
        props: {
            post: {
                ...post,
                blocks,
                menu: menu || null,
            },
        },
        revalidate: REVALIDATION_INTERVAL,
    };
}

// TODO: enable if we figure out PFS client side rendering
export async function getStaticPaths() {
    return {
        paths: [],
        fallback: 'blocking',
    };
}

export default Post;
